import {
  faro,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  withFaroRouterInstrumentation,
} from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { propagation } from "@opentelemetry/api";
import type { createBrowserRouter } from "react-router-dom";

export function initializeObservability() {
  // Verifica se as variáveis de ambiente estão definidas
  const faroUrl = import.meta.env.VITE_FARO_URL;

  if (!faroUrl) {
    console.warn(
      "VITE_FARO_URL não está definido. Observabilidade desabilitada.",
    );
    return null;
  }

  const appName = import.meta.env.VITE_FARO_APP_NAME || "wedo-poc";
  const appVersion = import.meta.env.VITE_FARO_APP_VERSION || "1.0.0";
  const environment = import.meta.env.VITE_FARO_ENVIRONMENT || "production";

  // Configura o W3C Trace Context Propagator ANTES de inicializar o Faro
  // Isso garante que os headers traceparent e tracestate sejam propagados corretamente
  propagation.setGlobalPropagator(new W3CTraceContextPropagator());

  initializeFaro({
    url: faroUrl,
    app: {
      name: appName,
      version: appVersion,
      environment: environment,
    },

    instrumentations: [
      // Mandatory, omits default instrumentations otherwise.
      ...getWebInstrumentations({
        captureConsole: true,
        captureConsoleDisabledLevels: [],
      }),

      // Tracing package to get end-to-end visibility for HTTP requests.
      new TracingInstrumentation({
        instrumentations: [
          // Instrumenta fetch API
          new FetchInstrumentation({
            // Ignora requisições para grafana.net (evita instrumentar telemetria)
            ignoreUrls: [/grafana\.net/],
            propagateTraceHeaderCorsUrls: [
              // Propaga headers de tracing para a API principal
              new RegExp(import.meta.env.VITE_API_URL || ".*"),
              // Propaga para todas as URLs
              /.*/,
            ],
            clearTimingResources: true,
          }),
          // Instrumenta XMLHttpRequest
          new XMLHttpRequestInstrumentation({
            // Ignora requisições para grafana.net (evita instrumentar telemetria)
            ignoreUrls: [/grafana\.net/],
            propagateTraceHeaderCorsUrls: [
              new RegExp(import.meta.env.VITE_API_URL || ".*"),
              /.*/,
            ],
          }),
        ],
      }),

      // React integration for React applications.
      new ReactIntegration(),
    ],
  });

  console.log(
    `Grafana Faro inicializado - App: ${appName}, Env: ${environment}`,
  );

  return withFaroRouterInstrumentation;
}

export function instrumentRouter(
  router: ReturnType<typeof createBrowserRouter>,
) {
  const instrumentFn = initializeObservability();

  if (!instrumentFn) {
    return router;
  }

  return instrumentFn(router);
}

// Vincula a sessão do BetterAuth com o Grafana Faro
export function setFaroUser(user: {
  id: string;
  email?: string;
  name?: string | null;
  sessionId?: string;
}) {
  if (!faro || !faro.api) {
    console.warn("Faro não está inicializado. Não foi possível setar usuário.");
    return;
  }

  try {
    // Define o usuário no Faro
    faro.api.setUser({
      id: user.id,
      email: user.email || undefined,
      username: user.name || undefined,
      attributes: {
        sessionId: user.sessionId || "unknown",
      },
    });

    // Registra evento de sessão vinculada
    faro.api.pushEvent("betterauth_session_linked", {
      userId: user.id,
      email: user.email || "not_provided",
      sessionId: user.sessionId || "unknown",
      timestamp: new Date().toISOString(),
    });

    console.log(
      `Faro: Usuário vinculado - ID: ${user.id}, Session: ${user.sessionId}`,
    );
  } catch (error) {
    console.error("Erro ao vincular usuário no Faro:", error);
  }
}

// Remove o usuário do Faro (útil no logout)
export function clearFaroUser() {
  if (!faro || !faro.api) {
    return;
  }

  try {
    faro.api.setUser({
      id: "anonymous",
    });

    faro.api.pushEvent("betterauth_session_cleared", {
      timestamp: new Date().toISOString(),
    });

    console.log("Faro: Sessão de usuário limpa");
  } catch (error) {
    console.error("Erro ao limpar usuário no Faro:", error);
  }
}
